// Login + first-admin-setup screens and the world picker (plain DOM, no
// framework — same as the vendor game).
//
//   GET /api/auth/me -> already logged in? skip straight to the game.
//   GET /api/auth/registration-open -> { open } decides the screen:
//     open  => "Creează contul de administrator" (POST /api/auth/register)
//     closed => email+password login (POST /api/auth/login)
//   Admins additionally get a small "Utilizatori" panel:
//     GET /api/auth/users (list) + POST /api/auth/users (create).
//
// Visual language: bold, playful, rounded, colorful — match the teaching-games
// dashboard aesthetic (gradients, big emoji, rounded corners, Fredoka).
//
// This file is the impure half (DOM + fetch). Validation and the
// error-code -> Romanian message map live in `auth-forms.ts` (auth) and
// `worlds-view.ts` (the world picker), which are pure and covered by their
// sibling `*.test.mjs` files. The picker's DOM behaviour itself is driven
// headless by `world-picker.test.mjs` over `ui/fake-dom.mjs`.
//
// No three.js here — the renderer (`render/renderer.ts`) is the only place
// allowed to import `three/webgpu`, and it never gets imported by this screen.
import {
  type AuthScreen,
  type Credentials,
  describeError,
  screenCopy,
  screenFor,
  validateCredentials
} from './auth-forms.js'
import {
  type WorldSummary,
  WORLDS_POLL_MS,
  BACKUP_DOWNLOAD_ONE,
  BACKUP_FILENAME,
  BACKUP_HINT,
  BACKUP_LABEL,
  BACKUP_TITLE,
  BACKUP_URL,
  BACKUPS_EMPTY,
  BACKUPS_ERROR,
  BACKUPS_URL,
  backupHref,
  backupRowLabel,
  parseBackupsPayload,
  HIDDEN_DONE_MESSAGE,
  HIDE_LABEL,
  RENAME_DONE_MESSAGE,
  RENAME_LABEL,
  RENAME_PLACEHOLDER,
  RENAME_SAVE_LABEL,
  RESTORE_LABEL,
  RESTORED_DONE_MESSAGE,
  describeWorldError,
  parseWorldSummary,
  parseWorldsPayload,
  validateWorldName,
  worldMeta
} from './worlds-view.js'

export interface SessionUser {
  userId: number
  email: string
  isAdmin: boolean
}

interface UserRow {
  id: number
  email: string
  isAdmin: boolean
}

/** Every call is same-origin through the vite proxy, but be explicit. */
const FETCH_OPTS: RequestInit = { credentials: 'include' }

async function getJson<T>(url: string): Promise<{ status: number; body: T | { error?: string } }> {
  const res = await fetch(url, FETCH_OPTS)
  const body = (await res.json().catch(() => ({}))) as T | { error?: string }
  return { status: res.status, body }
}

async function postJson<T>(
  url: string,
  payload: unknown
): Promise<{ status: number; body: T | { error?: string } }> {
  const res = await fetch(url, {
    ...FETCH_OPTS,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload)
  })
  const body = (await res.json().catch(() => ({}))) as T | { error?: string }
  return { status: res.status, body }
}

/** Like postJson, for the admin world routes (PATCH/DELETE need a method). */
async function sendJson<T>(
  method: string,
  url: string,
  payload?: unknown
): Promise<{ status: number; body: T | { error?: string } }> {
  const res = await fetch(url, {
    ...FETCH_OPTS,
    method,
    ...(payload === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) })
  })
  const body = (await res.json().catch(() => ({}))) as T | { error?: string }
  return { status: res.status, body }
}

/** null when logged out. Any network failure is treated as logged out. */
export async function fetchMe(): Promise<SessionUser | null> {
  try {
    const { status, body } = await getJson<SessionUser>('/api/auth/me')
    return status === 200 ? (body as SessionUser) : null
  } catch {
    return null
  }
}

/** True while the users table is empty (i.e. show the first-admin screen). */
export async function fetchRegistrationOpen(): Promise<boolean> {
  const { body } = await getJson<{ open: boolean }>('/api/auth/registration-open')
  return (body as { open?: boolean }).open === true
}

// ---------------------------------------------------------------- DOM helpers

type Attrs = Record<string, string>

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'style') node.style.cssText = v
    else node.setAttribute(k, v)
  }
  for (const c of children) node.append(c)
  return node
}

const CARD_STYLE = [
  'width:min(28rem,92vw)',
  'padding:2rem',
  'border-radius:1.75rem',
  'background:linear-gradient(160deg,#4f46e5 0%,#7c3aed 55%,#db2777 100%)',
  'box-shadow:0 24px 60px rgba(0,0,0,0.45)',
  'color:#fff',
  'display:flex',
  'flex-direction:column',
  'gap:0.9rem',
  'font-family:Fredoka,system-ui,sans-serif'
].join(';')

const INPUT_STYLE = [
  'width:100%',
  'box-sizing:border-box',
  'padding:0.9rem 1.1rem',
  'border:none',
  'border-radius:1rem',
  'font-size:1.05rem',
  'font-family:inherit',
  'background:rgba(255,255,255,0.94)',
  'color:#1e1b4b'
].join(';')

const BUTTON_STYLE = [
  'width:100%',
  'padding:0.95rem 1.1rem',
  'border:none',
  'border-radius:1rem',
  'font-size:1.1rem',
  'font-weight:700',
  'font-family:inherit',
  'cursor:pointer',
  'color:#3b0764',
  'background:linear-gradient(120deg,#fde047,#fb923c)',
  'box-shadow:0 8px 20px rgba(0,0,0,0.25)'
].join(';')

function field(label: string, type: string, autocomplete: string): {
  wrap: HTMLElement
  input: HTMLInputElement
} {
  const input = el('input', { type, autocomplete, style: INPUT_STYLE })
  const wrap = el('label', { style: 'display:flex;flex-direction:column;gap:0.35rem;font-size:0.95rem;' }, [
    el('span', { style: 'opacity:0.9;' }, [label]),
    input
  ])
  return { wrap, input }
}

// ---------------------------------------------------------------- the screens

/**
 * Render the login / first-admin screen into `host` and resolve with the
 * signed-in user. Stays on screen (showing Romanian errors) until it succeeds.
 */
export function mountAuthScreen(host: HTMLElement, screen: AuthScreen): Promise<SessionUser> {
  const copy = screenCopy(screen)
  host.replaceChildren()
  host.classList.remove('hidden')
  host.style.cssText = 'display:grid;place-items:center;min-height:100vh;width:100%;'

  const email = field('Email', 'email', 'username')
  const password = field('Parolă', 'password', screen === 'setup' ? 'new-password' : 'current-password')
  const error = el('p', {
    role: 'alert',
    style: 'margin:0;min-height:1.4rem;font-size:0.95rem;color:#fde68a;font-weight:600;'
  })
  const submit = el('button', { type: 'submit', style: BUTTON_STYLE }, [copy.submit])

  // novalidate: keep type="email" for the mobile keyboard, but suppress the
  // browser's native bubble+:invalid ring — it blocks submit, so our RO
  // error map in validateCredentials would never run.
  const form = el('form', { novalidate: '', style: 'display:flex;flex-direction:column;gap:0.9rem;' }, [
    email.wrap,
    password.wrap,
    error,
    submit
  ])

  const card = el('section', { style: CARD_STYLE }, [
    el('h1', { style: 'margin:0;font-size:1.9rem;line-height:1.15;' }, [copy.title]),
    el('p', { style: 'margin:0 0 0.4rem;opacity:0.92;' }, [copy.subtitle]),
    form
  ])
  host.append(card)

  return new Promise<SessionUser>(resolve => {
    form.addEventListener('submit', event => {
      event.preventDefault()
      const check = validateCredentials(email.input.value, password.input.value)
      if (!check.ok) {
        error.textContent = check.message
        ;(check.field === 'email' ? email.input : password.input).focus()
        return
      }
      error.textContent = ''
      submit.disabled = true
      submit.textContent = 'Un moment… ⏳'

      void submitCredentials(screen, check.value)
        .then(result => {
          if (result.user) {
            resolve(result.user)
            return
          }
          error.textContent = result.message ?? describeError(undefined)
          submit.disabled = false
          submit.textContent = copy.submit
        })
        .catch(() => {
          error.textContent = describeError('network')
          submit.disabled = false
          submit.textContent = copy.submit
        })
    })
    email.input.focus()
  })
}

async function submitCredentials(
  screen: AuthScreen,
  creds: Credentials
): Promise<{ user?: SessionUser; message?: string }> {
  const url = screen === 'setup' ? '/api/auth/register' : '/api/auth/login'
  const { status, body } = await postJson<SessionUser>(url, creds)
  if (status === 200 || status === 201) return { user: body as SessionUser }
  return { message: describeError((body as { error?: string }).error) }
}

/**
 * The admin-only "Utilizatori" panel: the list plus a create-account form.
 * Appended to `host`; a non-admin never gets here (the server would 403 anyway).
 */
export function mountUsersPanel(host: HTMLElement): HTMLElement {
  const list = el('ul', { style: 'list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:0.4rem;' })
  const email = field('Email pentru contul nou', 'email', 'off')
  const password = field('Parolă (minim 6 caractere)', 'password', 'new-password')
  const note = el('p', { role: 'status', style: 'margin:0;min-height:1.4rem;font-size:0.95rem;font-weight:600;color:#fde68a;' })
  const submit = el('button', { type: 'submit', style: BUTTON_STYLE }, ['Adaugă un jucător ➕'])
  const form = el('form', { novalidate: '', style: 'display:flex;flex-direction:column;gap:0.7rem;margin-top:0.6rem;' }, [
    email.wrap,
    password.wrap,
    note,
    submit
  ])

  const panel = el('section', { style: CARD_STYLE }, [
    el('h2', { style: 'margin:0;font-size:1.4rem;' }, ['Utilizatori 👨‍👩‍👧‍👦']),
    list,
    form
  ])
  host.append(panel)

  const render = (users: UserRow[]): void => {
    list.replaceChildren(
      ...users.map(u =>
        el('li', { style: 'background:rgba(255,255,255,0.14);border-radius:0.9rem;padding:0.6rem 0.9rem;' }, [
          `${u.isAdmin ? '👑' : '🙂'} ${u.email}`
        ])
      )
    )
  }

  const refresh = async (): Promise<void> => {
    try {
      const { status, body } = await getJson<UserRow[]>('/api/auth/users')
      if (status === 200 && Array.isArray(body)) render(body as UserRow[])
    } catch {
      note.textContent = describeError('network')
    }
  }
  void refresh()

  form.addEventListener('submit', event => {
    event.preventDefault()
    const check = validateCredentials(email.input.value, password.input.value)
    if (!check.ok) {
      note.textContent = check.message
      ;(check.field === 'email' ? email.input : password.input).focus()
      return
    }
    note.textContent = ''
    submit.disabled = true

    void postJson<UserRow>('/api/auth/users', check.value)
      .then(async ({ status, body }) => {
        if (status === 201) {
          note.textContent = `Contul ${(body as UserRow).email} e gata! ✅`
          email.input.value = ''
          password.input.value = ''
          await refresh()
        } else {
          note.textContent = describeError((body as { error?: string }).error)
        }
      })
      .catch(() => {
        note.textContent = describeError('network')
      })
      .finally(() => {
        submit.disabled = false
      })
  })

  return panel
}

/** Injectable seam so `backup-panel.test.mjs` can drive the card headless. */
export interface BackupPanelDeps {
  listBackups?: () => Promise<{ status: number; body: unknown }>
}

const BACKUP_ROW_STYLE = [
  'display:flex',
  'align-items:center',
  'justify-content:space-between',
  'gap:0.6rem',
  'padding:0.5rem 0.75rem',
  'border-radius:0.75rem',
  'background:rgba(255,255,255,0.12)',
  'font-size:0.9rem'
].join(';')

/**
 * The admin-only backup card: the big button downloads the NEWEST copy, and
 * below it every retained copy is listed (UTC stamp + size) with its own ⬇️
 * link. Every download is an ANCHOR — the browser streams the multi-MB db; a
 * fetch would hold it in a JS blob. Each per-copy href carries the server's own
 * listed `name`, which the server then membership-tests against the rotation:
 * the client never picks a path. A kid never sees this card (403 anyway).
 */
export function mountBackupPanel(host: HTMLElement, deps: BackupPanelDeps = {}): HTMLElement {
  const listBackups = deps.listBackups ?? (() => getJson<unknown>(BACKUPS_URL))

  const link = el(
    'a',
    {
      href: BACKUP_URL,
      download: BACKUP_FILENAME,
      style: BUTTON_STYLE + ';display:block;text-align:center;text-decoration:none;'
    },
    [BACKUP_LABEL]
  )
  const list = el('div', { style: 'display:flex;flex-direction:column;gap:0.4rem;' })
  const panel = el('section', { style: CARD_STYLE }, [
    el('h2', { style: 'margin:0;font-size:1.4rem;' }, [BACKUP_TITLE]),
    el('p', { style: 'margin:0;opacity:0.92;font-size:0.95rem;' }, [BACKUP_HINT]),
    link,
    list
  ])
  host.append(panel)

  const note = (text: string): void => {
    list.replaceChildren(el('p', { style: 'margin:0;opacity:0.85;font-size:0.9rem;' }, [text]))
  }

  void listBackups()
    .then(({ status, body }) => {
      const entries = status === 200 ? parseBackupsPayload(body) : null
      if (entries === null) return note(BACKUPS_ERROR)
      if (entries.length === 0) return note(BACKUPS_EMPTY)
      list.replaceChildren(
        ...entries.map(entry =>
          el('div', { style: BACKUP_ROW_STYLE }, [
            el('span', {}, [backupRowLabel(entry)]),
            el(
              'a',
              {
                href: backupHref(entry.name),
                download: entry.name,
                title: entry.name,
                style: 'text-decoration:none;font-size:1.1rem;'
              },
              [BACKUP_DOWNLOAD_ONE]
            )
          ])
        )
      )
    })
    .catch(() => note(BACKUPS_ERROR))

  return panel
}

// ---------------------------------------------------------------- the worlds

/** Injectable seams so `world-picker.test.mjs` can drive the picker headless. */
export interface WorldPickerDeps {
  listWorlds?: () => Promise<{ status: number; body: unknown }>
  createWorld?: (name: string) => Promise<{ status: number; body: unknown }>
  /** True renders the per-row admin controls (rename / hide / restore). */
  admin?: boolean
  renameWorld?: (id: number, name: string) => Promise<{ status: number; body: unknown }>
  hideWorld?: (id: number) => Promise<{ status: number; body: unknown }>
  restoreWorld?: (id: number) => Promise<{ status: number; body: unknown }>
  setIntervalFn?: (fn: () => void, ms: number) => unknown
  clearIntervalFn?: (handle: unknown) => void
}

const ROW_STYLE = [
  'width:100%',
  'display:flex',
  'flex-direction:column',
  'align-items:flex-start',
  'gap:0.15rem',
  'padding:0.75rem 1rem',
  'border:none',
  'border-radius:1rem',
  'font-family:inherit',
  'text-align:left',
  'cursor:pointer',
  'color:#fff',
  'background:rgba(255,255,255,0.14)'
].join(';')

const NOTE_STYLE = 'margin:0;min-height:1.4rem;font-size:0.95rem;font-weight:600;color:#fde68a;'

/**
 * The world picker (M6, task 18b): the live list from GET /api/worlds plus the
 * "Lume nouă" create form. Resolves with the picked world id — the caller
 * hands it to `startWorld(worldId)`.
 *
 * The list re-polls every WORLDS_POLL_MS while on screen and STOPS the moment
 * a world is picked. A poll tick MUTATES the existing row nodes (keyed by
 * world id) — textContent only — and never rebuilds or reorders the list: a
 * repaint between mousedown and mouseup silently eats the click (the
 * pause-menu lesson). New worlds are appended; the API lists id-ASC and ids
 * only grow, so append preserves order without ever moving an existing node.
 */
export function mountWorldPicker(host: HTMLElement, deps: WorldPickerDeps = {}): Promise<number> {
  const listWorlds = deps.listWorlds ?? (() => getJson<WorldSummary[]>('/api/worlds'))
  const createWorld =
    deps.createWorld ?? ((name: string) => postJson<WorldSummary>('/api/worlds', { name }))
  const admin = deps.admin === true
  const renameWorld =
    deps.renameWorld ?? ((id: number, name: string) => sendJson<WorldSummary>('PATCH', `/api/worlds/${id}`, { name }))
  const hideWorld = deps.hideWorld ?? ((id: number) => sendJson<WorldSummary>('DELETE', `/api/worlds/${id}`))
  const restoreWorld =
    deps.restoreWorld ?? ((id: number) => sendJson<WorldSummary>('POST', `/api/worlds/${id}/restore`))
  const setIntervalFn = deps.setIntervalFn ?? ((fn: () => void, ms: number) => setInterval(fn, ms))
  const clearIntervalFn = deps.clearIntervalFn ?? ((handle: unknown) => clearInterval(handle as never))

  const status = el('p', { role: 'status', style: NOTE_STYLE })
  const empty = el('p', { style: 'margin:0;opacity:0.92;' }, [
    'Nicio lume încă — creează prima! 👇'
  ])
  const list = el('ul', {
    style: 'list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:0.5rem;'
  })

  const nameInput = el('input', {
    type: 'text',
    autocomplete: 'off',
    placeholder: 'Numele lumii noi',
    style: INPUT_STYLE
  })
  const note = el('p', { role: 'status', style: NOTE_STYLE })
  const submit = el('button', { type: 'submit', style: BUTTON_STYLE }, ['Lume nouă ✨'])
  const form = el('form', { novalidate: '', style: 'display:flex;flex-direction:column;gap:0.7rem;margin-top:0.4rem;' }, [
    nameInput,
    note,
    submit
  ])

  const card = el('section', { style: CARD_STYLE }, [
    el('h2', { style: 'margin:0;font-size:1.4rem;' }, ['Alege o lume 🌍']),
    status,
    empty,
    list,
    form
  ])
  host.append(card)

  /** Filled in by the Promise executor below (which runs synchronously). */
  let pick: (worldId: number) => void = () => {}

  // One row node per world id, created ONCE and only ever mutated. Never
  // rebuild these on a poll tick — node identity is what keeps clicks alive.
  // The admin controls (rename / hide / restore) follow the same rule: built
  // with the row, repainted via textContent/style only.
  interface WorldRow {
    item: HTMLLIElement
    button: HTMLButtonElement
    name: HTMLElement
    meta: HTMLElement
    /** Latest known summary — the admin handlers act on the CURRENT state. */
    world: WorldSummary
    toggle?: HTMLButtonElement
    renameBox?: HTMLElement
    renameInput?: HTMLInputElement
    adminNote?: HTMLElement
  }
  const rows = new Map<number, WorldRow>()

  /** textContent/style only — the one legal way to update a live row. */
  const paintRow = (row: WorldRow): void => {
    row.name.textContent = row.world.name
    row.meta.textContent = worldMeta(row.world)
    row.button.style.opacity = row.world.hidden ? '0.55' : '1'
    if (row.toggle) row.toggle.textContent = row.world.hidden ? RESTORE_LABEL : HIDE_LABEL
  }

  /** Admin action plumbing: run the request, repaint from the 200 summary.
   *  Resolves true on success so the caller can close its inline UI. */
  const runAdminAction = (
    row: WorldRow,
    action: () => Promise<{ status: number; body: unknown }>,
    doneMessage: string
  ): Promise<boolean> =>
    action()
      .then(({ status: code, body }) => {
        const world = code === 200 ? parseWorldSummary(body) : null
        if (world) {
          row.world = world
          paintRow(row)
          row.adminNote!.textContent = doneMessage
          return true
        }
        row.adminNote!.textContent = describeWorldError((body as { error?: string }).error)
        return false
      })
      .catch(() => {
        row.adminNote!.textContent = describeWorldError('network')
        return false
      })

  const ADMIN_BTN_STYLE = [
    'border:none',
    'border-radius:0.7rem',
    'padding:0.35rem 0.7rem',
    'font-family:inherit',
    'font-size:0.85rem',
    'font-weight:600',
    'cursor:pointer',
    'color:#fff',
    'background:rgba(255,255,255,0.22)'
  ].join(';')

  /** The admin strip under a row: rename toggle + hide/restore + inline box. */
  const buildAdminControls = (row: WorldRow): HTMLElement => {
    const renameToggle = el('button', { type: 'button', style: ADMIN_BTN_STYLE }, [RENAME_LABEL])
    const toggle = el('button', { type: 'button', style: ADMIN_BTN_STYLE }, [
      row.world.hidden ? RESTORE_LABEL : HIDE_LABEL
    ])
    const input = el('input', {
      type: 'text',
      autocomplete: 'off',
      placeholder: RENAME_PLACEHOLDER,
      style: INPUT_STYLE + ';padding:0.5rem 0.8rem;font-size:0.9rem;'
    })
    const save = el('button', { type: 'button', style: ADMIN_BTN_STYLE }, [RENAME_SAVE_LABEL])
    // Deliberately a DIV, not a <form>: Enter-to-submit isn't needed and a
    // nested form per row would shadow the create form for tests and a11y.
    const renameBox = el('div', { style: 'display:none;gap:0.4rem;margin-top:0.3rem;' }, [input, save])
    const adminNote = el('p', { role: 'status', style: 'margin:0;min-height:0;font-size:0.8rem;font-weight:600;color:#fde68a;' })
    const strip = el('div', { style: 'display:flex;gap:0.4rem;margin-top:0.35rem;flex-wrap:wrap;' }, [
      renameToggle,
      toggle
    ])

    row.toggle = toggle
    row.renameBox = renameBox
    row.renameInput = input
    row.adminNote = adminNote

    renameToggle.addEventListener('click', () => {
      const open = renameBox.style.display !== 'none'
      renameBox.style.display = open ? 'none' : 'flex'
      if (!open) input.value = row.world.name
    })
    save.addEventListener('click', () => {
      const check = validateWorldName(input.value)
      if (!check.ok) {
        adminNote.textContent = check.message
        return
      }
      save.disabled = true
      void runAdminAction(row, () => renameWorld(row.world.id, check.value), RENAME_DONE_MESSAGE)
        .then(ok => {
          if (ok) renameBox.style.display = 'none'
        })
        .finally(() => {
          save.disabled = false
        })
    })
    toggle.addEventListener('click', () => {
      toggle.disabled = true
      const hidden = row.world.hidden
      void runAdminAction(
        row,
        () => (hidden ? restoreWorld(row.world.id) : hideWorld(row.world.id)),
        hidden ? RESTORED_DONE_MESSAGE : HIDDEN_DONE_MESSAGE
      ).finally(() => {
        toggle.disabled = false
      })
    })

    return el('div', {}, [strip, renameBox, adminNote])
  }

  const upsertRow = (world: WorldSummary): void => {
    const existing = rows.get(world.id)
    if (existing) {
      existing.world = world
      paintRow(existing)
      return
    }
    const name = el('span', { style: 'font-weight:700;font-size:1.05rem;' }, [world.name])
    const meta = el('span', { style: 'opacity:0.85;font-size:0.85rem;' }, [worldMeta(world)])
    const button = el('button', { type: 'button', style: ROW_STYLE }, [name, meta])
    button.addEventListener('click', () => pick(world.id))
    const item = el('li', {}, [button])
    const row: WorldRow = { item, button, name, meta, world }
    if (admin) item.append(buildAdminControls(row))
    rows.set(world.id, row)
    paintRow(row)
    list.append(item)
    empty.style.display = 'none'
  }

  const render = (worlds: WorldSummary[]): void => {
    const seen = new Set(worlds.map(w => w.id))
    for (const [id, row] of rows) {
      if (!seen.has(id)) {
        row.item.remove()
        rows.delete(id)
      }
    }
    for (const world of worlds) upsertRow(world)
    empty.style.display = rows.size === 0 ? '' : 'none'
  }

  let inFlight = false
  const refresh = async (): Promise<void> => {
    if (inFlight) return
    inFlight = true
    try {
      const { status: code, body } = await listWorlds()
      const worlds = code === 200 ? parseWorldsPayload(body) : null
      if (worlds) {
        render(worlds)
        status.textContent = ''
      } else {
        // Keep the last good list on screen; just say the refresh failed.
        status.textContent = describeWorldError(
          code === 200 ? 'network' : (body as { error?: string }).error ?? 'network'
        )
      }
    } catch {
      status.textContent = describeWorldError('network')
    } finally {
      inFlight = false
    }
  }

  void refresh()
  const timer = setIntervalFn(() => void refresh(), WORLDS_POLL_MS)

  form.addEventListener('submit', event => {
    event.preventDefault()
    const check = validateWorldName(nameInput.value)
    if (!check.ok) {
      note.textContent = check.message
      return
    }
    note.textContent = ''
    submit.disabled = true
    void createWorld(check.value)
      .then(({ status: code, body }) => {
        if (code === 201) {
          const world = parseWorldSummary(body)
          if (world) upsertRow(world)
          note.textContent = `Lumea „${check.value}” e gata! Apas-o ca să intri. ✅`
          nameInput.value = ''
        } else {
          note.textContent = describeWorldError((body as { error?: string }).error)
        }
      })
      .catch(() => {
        note.textContent = describeWorldError('network')
      })
      .finally(() => {
        submit.disabled = false
      })
  })

  return new Promise<number>(resolve => {
    pick = worldId => {
      clearIntervalFn(timer)
      resolve(worldId)
    }
  })
}

/**
 * Full flow: already-signed-in short-circuit, otherwise pick the screen from
 * `registration-open` and wait for the user to get in.
 */
export async function requireSession(host: HTMLElement): Promise<SessionUser> {
  const me = await fetchMe()
  if (me) return me

  let open = false
  try {
    open = await fetchRegistrationOpen()
  } catch {
    open = false
  }
  return mountAuthScreen(host, screenFor(open))
}
